import { useLingui } from '@lingui/react/macro';
import React, { useCallback } from 'react';
import UDButton from '../button';
import UDModal, { UDModalSize } from './modal';
import { ModalFooter, ModalHeader } from './modal-parts';

export interface UDConfirmModalProps {
  show: boolean;

  /** Вопрос по канону: «Удалить команду «Метеор»?» */
  title: string;
  /** Пояснение под заголовком. Обычно «Это действие необратимо.» */
  description?: string;

  /** Подпись кнопки действия — глагол: «Удалить», «Исключить», «Покинуть». */
  confirmText?: string;
  cancelText?: string;

  isLoading?: boolean;

  onConfirm: () => void | Promise<unknown>;
  /** Закрытие окна: и кнопкой отмены, и крестиком, и Escape, и кликом по затемнению. */
  onCancel: () => void;

  size?: UDModalSize;

  disableEscapeKey?: boolean;
  disableOverlayClick?: boolean;
}

/**
 * Каноническое окно подтверждения: заголовок и пояснение по центру,
 * «Отмена» (secondary) слева, действие (primary) справа, обе на пол-ширины.
 *
 * Окно НЕ закрывает себя после `onConfirm` — закрывает вызывающая сторона,
 * когда операция действительно удалась. Иначе окно исчезало бы и при ошибке:
 * обработчики удаления ловят ошибку сами и возвращают выполненный промис.
 */
const UDConfirmModal: React.FC<UDConfirmModalProps> = ({
  show,
  title,
  description,
  confirmText,
  cancelText,
  isLoading,
  onConfirm,
  onCancel,
  size = 'sm',
  disableEscapeKey,
  disableOverlayClick,
}) => {
  const { t } = useLingui();
  const handleConfirm = useCallback(async () => {
    await onConfirm();
  }, [onConfirm]);

  return (
    <UDModal show={show} onClose={onCancel} size={size} disableEscapeKey={disableEscapeKey} disableOverlayClick={disableOverlayClick}>
      <ModalHeader title={title} subtitle={description} />
      <ModalFooter>
        <UDButton variant="secondary" onClick={onCancel} disabled={isLoading} stretch>
          {cancelText ?? t`Отмена`}
        </UDButton>
        <UDButton variant="primary" onClick={handleConfirm} loading={Boolean(isLoading)} stretch>
          {confirmText ?? t`Удалить`}
        </UDButton>
      </ModalFooter>
    </UDModal>
  );
};

export default UDConfirmModal;
